import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageHero } from '../components/PageHero';
import { toast } from 'sonner';
import { Coins, TrendingUp, Lock, Unlock, Zap, Award, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_STYLES = {
    '30d': { color: '#CD7F32', gradient: 'from-orange-600/20 to-orange-900/20', icon: Award },
    '90d': { color: '#C0C0C0', gradient: 'from-gray-400/20 to-gray-700/20', icon: Zap },
    '365d': { color: '#FFD700', gradient: 'from-yellow-500/20 to-yellow-900/20', icon: TrendingUp },
};

export default function StakingPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [plans, setPlans] = useState([]);
    const [stats, setStats] = useState(null);
    const [summary, setSummary] = useState(null);
    const [boost, setBoost] = useState(null);
    const [amounts, setAmounts] = useState({});
    const [loading, setLoading] = useState({});

    const loadAll = async () => {
        try {
            const [p, s] = await Promise.all([
                fetch(`${API}/api/staking/plans`).then(r => r.json()),
                fetch(`${API}/api/staking/stats`).then(r => r.json()),
            ]);
            setPlans(p.plans || []);
            setStats(s);
            if (address) {
                const [sum, bst] = await Promise.all([
                    fetch(`${API}/api/staking/summary/${address}`).then(r => r.json()),
                    fetch(`${API}/api/staking/boost/${address}`).then(r => r.json()),
                ]);
                setSummary(sum);
                setBoost(bst);
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => { loadAll(); const i = setInterval(loadAll, 20000); return () => clearInterval(i); }, [address]);

    const handleStake = async (planId) => {
        if (!address) return toast.error('Connectez votre wallet');
        const amt = parseFloat(amounts[planId] || 0);
        if (!amt || amt <= 0) return toast.error('Montant B4P invalide');
        setLoading(l => ({ ...l, [planId]: true }));
        try {
            const res = await fetch(`${API}/api/staking/stake`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, plan_id: planId, amount: amt }),
            });
            const data = await res.json();
            if (data.success) {
                toast.success(`${amt} B4P stakés sur ${planId}`);
                setAmounts(a => ({ ...a, [planId]: '' }));
                loadAll();
            } else {
                toast.error(data.error || 'Échec du stake');
            }
        } catch (e) { toast.error('Erreur réseau'); }
        setLoading(l => ({ ...l, [planId]: false }));
    };

    const handleUnstake = async (stakeId) => {
        if (!address) return;
        try {
            const res = await fetch(`${API}/api/staking/unstake`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, stake_id: stakeId }),
            });
            const data = await res.json();
            if (data.success) {
                if (data.early) toast.warning(`Unstake anticipé: ${data.returned} B4P récupérés (pénalité ${data.penalty} B4P)`);
                else toast.success(`Unstake: ${data.returned} B4P (+${data.rewards} récompenses) crédités`);
                loadAll();
            } else toast.error(data.error);
        } catch (e) { toast.error('Erreur réseau'); }
    };

    return (
        <div className="min-h-screen" data-testid="staking-page">
            <PageHero
                icon={Coins}
                title={t('staking.title')}
                highlight={t('staking.highlight')}
                subtitle={t('staking.subtitle')}
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

                {/* Global Stats */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">TVL total</div>
                                <div className="text-2xl font-bold text-[#00FF66]" data-testid="staking-tvl">{stats.tvl_b4p?.toLocaleString()} B4P</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">Stakes actifs</div>
                                <div className="text-2xl font-bold text-white">{stats.active_stakes}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">Récompenses distribuées</div>
                                <div className="text-2xl font-bold text-[#FFD700]">{stats.rewards_b4p?.toLocaleString()} B4P</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">Pénalité anticipée</div>
                                <div className="text-2xl font-bold text-red-400">{stats.early_unstake_penalty_percent}%</div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Active Pack APY Boost Banner */}
                {boost?.pack && boost.staking_apy_boost_pct > 0 && (
                    <Card className="bg-gradient-to-r from-[#FFD700]/15 via-[#003B71]/30 to-[#FFD700]/15 border-[#FFD700]/40 mb-6" data-testid="staking-boost-banner">
                        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${boost.pack.color || '#FFD700'}25`, border: `1px solid ${boost.pack.color || '#FFD700'}80` }}>
                                    <Sparkles className="w-6 h-6" style={{ color: boost.pack.color || '#FFD700' }} />
                                </div>
                                <div>
                                    <div className="font-bold text-white">
                                        Boost APY actif : <span className="text-[#FFD700]">+{boost.staking_apy_boost_pct}%</span>
                                    </div>
                                    <div className="text-xs text-white/70">
                                        Grâce à votre <span className="font-bold" style={{ color: boost.pack.color || '#FFD700' }}>{boost.pack.label}</span> équipé · Appliqué automatiquement sur chaque nouveau stake
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {boost.boosted_plans?.map(bp => (
                                    <Badge key={bp.plan_id} className="bg-[#FFD700]/15 border-[#FFD700]/40 text-[#FFD700] font-bold" data-testid={`boost-plan-${bp.plan_id}`}>
                                        {bp.label} : {bp.base_apy}% → {bp.boosted_apy}%
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
                {address && boost && !boost.pack && (
                    <Card className="bg-white/5 border-white/10 border-dashed mb-6" data-testid="staking-boost-cta">
                        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <Sparkles className="w-5 h-5 text-[#FFD700]" />
                                <div className="text-sm text-white/70">
                                    Équipez un <span className="text-[#FFD700] font-bold">NFT Booster Pack</span> pour gagner <span className="text-[#FFD700] font-bold">jusqu'à +30% d'APY</span> permanent sur votre staking.
                                </div>
                            </div>
                            <a href="/booster-packs" className="text-sm font-bold text-[#FFD700] hover:underline" data-testid="staking-boost-cta-link">Voir les packs →</a>
                        </CardContent>
                    </Card>
                )}

                {/* Plans */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {plans.map(plan => {
                        const style = PLAN_STYLES[plan.id] || PLAN_STYLES['30d'];
                        const Icon = style.icon;
                        const apyMult = boost?.staking_apy_multiplier || 1.0;
                        const boostedApy = +(plan.apy * apyMult).toFixed(2);
                        const hasBoost = apyMult > 1.0;
                        return (
                            <Card key={plan.id} className={`bg-gradient-to-br ${style.gradient} border-white/20 overflow-hidden`} data-testid={`plan-${plan.id}`}>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-white text-xl flex items-center gap-2">
                                            <Icon className="w-5 h-5" style={{ color: style.color }} />
                                            {plan.label}
                                        </CardTitle>
                                        <Badge style={{ background: `${style.color}20`, color: style.color, borderColor: `${style.color}50` }}>{plan.tier}</Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="mb-4">
                                        {hasBoost ? (
                                            <>
                                                <div className="flex items-baseline gap-2">
                                                    <div className="text-5xl font-black" style={{ color: style.color }}>{boostedApy}%</div>
                                                    <Badge className="bg-[#FFD700]/15 border-[#FFD700]/40 text-[#FFD700] font-bold text-xs" data-testid={`apy-boost-badge-${plan.id}`}>
                                                        +{boost.staking_apy_boost_pct}%
                                                    </Badge>
                                                </div>
                                                <div className="text-white/50 text-sm">
                                                    APY · compound quotidien · <span className="line-through opacity-60">{plan.apy}%</span> boosté
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="text-5xl font-black" style={{ color: style.color }}>{plan.apy}%</div>
                                                <div className="text-white/50 text-sm">APY · compound quotidien</div>
                                            </>
                                        )}
                                    </div>
                                    <div className="text-sm text-white/60 mb-4 space-y-1">
                                        <div className="flex justify-between"><span>Verrouillage</span><span className="text-white">{plan.lock_days} jours</span></div>
                                        <div className="flex justify-between"><span>Stakes actifs</span><span className="text-white">{plan.active_stakes}</span></div>
                                    </div>
                                    <Input
                                        type="number" placeholder="Montant B4P"
                                        value={amounts[plan.id] || ''}
                                        onChange={e => setAmounts(a => ({ ...a, [plan.id]: e.target.value }))}
                                        className="bg-black/30 border-white/20 text-white mb-3"
                                        data-testid={`stake-amount-${plan.id}`}
                                    />
                                    <Button
                                        onClick={() => handleStake(plan.id)}
                                        disabled={!address || loading[plan.id]}
                                        className="w-full font-bold"
                                        style={{ background: style.color, color: '#000' }}
                                        data-testid={`stake-btn-${plan.id}`}
                                    >
                                        <Lock className="w-4 h-4 mr-2" />
                                        {loading[plan.id] ? 'Staking...' : `Stake ${hasBoost ? boostedApy : plan.apy}% APY`}
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* My Stakes */}
                {summary && (
                    <Card className="bg-white/5 border-white/10">
                        <CardHeader><CardTitle className="text-white">Mes stakes ({summary.active_count} actifs)</CardTitle></CardHeader>
                        <CardContent>
                            {summary.active_count === 0 ? (
                                <div className="text-center py-6 text-white/40">Aucun stake actif. Lancez votre premier stake ci-dessus !</div>
                            ) : (
                                <div className="space-y-3">
                                    {summary.active_stakes.map(s => (
                                        <div key={s.stake_id} className="flex items-center justify-between p-4 bg-black/30 rounded-lg border border-white/10" data-testid={`my-stake-${s.stake_id}`}>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <Badge style={{ color: PLAN_STYLES[s.plan_id]?.color || '#fff' }}>{s.tier}</Badge>
                                                    <span className="text-white font-bold">{s.principal} B4P → {s.current_value?.toFixed(4)} B4P</span>
                                                </div>
                                                <div className="text-xs text-white/50 mt-1">APY {s.apy}% · Unlock: {new Date(s.unlock_at).toLocaleDateString('fr-FR')}</div>
                                            </div>
                                            <Button onClick={() => handleUnstake(s.stake_id)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`unstake-${s.stake_id}`}>
                                                <Unlock className="w-4 h-4 mr-1" />Unstake
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {summary.active_count > 0 && (
                                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
                                    <div><div className="text-xs text-white/50">Principal</div><div className="text-white font-bold">{summary.total_principal} B4P</div></div>
                                    <div><div className="text-xs text-white/50">Valeur actuelle</div><div className="text-[#00FF66] font-bold">{summary.total_value} B4P</div></div>
                                    <div><div className="text-xs text-white/50">Récompenses</div><div className="text-[#FFD700] font-bold">+{summary.accrued_rewards} B4P</div></div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
